/**
 * Firebase SDK 初期化
 *
 * クライアントサイドの Firebase SDK を初期化します。
 * Emulator 環境では自動的にエミュレータに接続します。
 */
import { initializeApp, FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, Firestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, Functions, httpsCallable } from "firebase/functions";
import { getStorage, connectStorageEmulator, FirebaseStorage } from "firebase/storage";

// Firebase 設定（環境変数から読み込み、未設定時はEmulator用デフォルト）
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "demo-project.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-project",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "demo-project.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "000000000000",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:000000000000:web:0000000000000000",
};

// Firebase インスタンス
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let functions: Functions;
let storage: FirebaseStorage;

// Emulator 環境かどうかを判定
const isEmulator =
  (import.meta.env.DEV ||
    (typeof window !== "undefined" &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"))) &&
  import.meta.env.VITE_USE_EMULATOR !== "false";

/**
 * Firebase を初期化
 */
export function initializeFirebase(): FirebaseApp {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  return app;
}

/**
 * Firebase Auth インスタンスを取得
 */
export function getAuthInstance(): Auth {
  if (!auth) {
    initializeFirebase();
    auth = getAuth(app);

    // Emulator 環境では Auth Emulator に接続
    if (isEmulator) {
      connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    }
  }
  return auth;
}

/**
 * Firestore インスタンスを取得
 */
export function getDbInstance(): Firestore {
  if (!db) {
    initializeFirebase();
    db = getFirestore(app);

    // Emulator 環境では Firestore Emulator に接続
    if (isEmulator) {
      connectFirestoreEmulator(db, "localhost", 8080);
    }
  }
  return db;
}

/**
 * Functions インスタンスを取得
 */
export function getFunctionsInstance(): Functions {
  if (!functions) {
    initializeFirebase();
    functions = getFunctions(app, "asia-northeast1"); // Set region to match backend

    if (isEmulator) {
      connectFunctionsEmulator(functions, "localhost", 5001);
    }
  }
  return functions;
}

/**
 * Firebase Storage インスタンスを取得
 */
export function getStorageInstance(): FirebaseStorage {
  if (!storage) {
    initializeFirebase();
    storage = getStorage(app);

    if (isEmulator) {
      connectStorageEmulator(storage, "localhost", 9199);
    }
  }
  return storage;
}

/**
 * Emulator 環境かどうかを返す
 */
export function isUsingEmulator(): boolean {
  return isEmulator;
}

export { httpsCallable };


